
import type React from "react"
import { useDroppable } from "@dnd-kit/core"
import { DraggableCard } from "./draggable-card"
import { sourceOf } from "@/components/dashboard/room/room-model"

// Two layouts:
//   "wide" — full-width pane that renders cards as a responsive grid. Used
//            inside the sub-tab pipeline view where one status fills the
//            page. Includes a fade-out gradient at the bottom so users know
//            the list scrolls when there are more cards than fit.
//   "grid" — legacy column-of-cards layout (still here in case it's reused
//            for narrower contexts; default behaviour for unknown variants).
interface DroppableColumnProps {
  id: string
  title?: string
  count?: number
  // Tailwind bg-* class for a small colored dot rendered before the title in
  // the grid variant. Used by the multi-column kanban to visually anchor each
  // status without changing the column's layout.
  accentClassName?: string
  estimates: any[]
  isNewEstimate?: (estimate: any) => boolean
  // When false, cards render but can't be dragged and the column won't
  // highlight on hover. Used to gate the kanban move-by-drag affordance to
  // body-shop teams only — dealership users see the board read-only.
  canMove?: boolean
  onViewDetails?: (estimateId: string) => void
  onCall?: (phone: string) => void
  onEmail?: (email: string) => void
  onSMS?: (phone: string) => void
  onConfirm?: (estimateId: string) => void
  // Optional action rendered in the column header (e.g. "Approve N clean").
  headerAction?: React.ReactNode
  // Small uppercase label above the title — used to mark system-driven lanes
  // ("Automatic") that advance on their own rather than by a human move.
  eyebrow?: string
  variant?: "wide" | "grid"
}

export function DroppableColumn({
  id,
  title,
  count,
  accentClassName,
  estimates,
  isNewEstimate,
  canMove = true,
  onViewDetails,
  onCall,
  onEmail,
  onSMS,
  onConfirm,
  headerAction,
  eyebrow,
  variant = "grid",
}: DroppableColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id, disabled: !canMove })

  if (variant === "wide") {
    return (
      <div
        className={`relative rounded-2xl border bg-white shadow-sm transition-all ${
          isOver ? "border-emerald-300 ring-2 ring-emerald-400/30 shadow-lg" : "border-zinc-200/60"
        }`}
      >
        <div
          ref={setNodeRef}
          className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 max-h-[calc(100vh-340px)] min-h-[280px] overflow-y-auto"
        >
          {estimates.map((estimate) => (
            <DraggableCard
              key={estimate.id}
              estimate={estimate}
              isNew={isNewEstimate?.(estimate)}
              isConsumerLead={sourceOf(estimate) === "consumer_intake"}
              isTotalLoss={estimate.outcome === "total_loss" || estimate.totalLoss === true}
              disabled={!canMove}
              onViewDetails={onViewDetails}
              onCall={onCall}
              onEmail={onEmail}
              onSMS={onSMS}
              onConfirm={onConfirm}
            />
          ))}
          {estimates.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
              <div className="size-12 rounded-2xl bg-zinc-100 grid place-items-center mb-3">
                <span className="text-zinc-300 text-xl" aria-hidden>
                  ✦
                </span>
              </div>
              <p className="text-sm font-medium text-zinc-700">Nothing here</p>
              <p className="text-xs text-zinc-400 mt-1">
                No {title?.toLowerCase() ?? "open"} estimates right now.
              </p>
            </div>
          )}
        </div>
        {/* Bottom fade — soft cue that the column scrolls when full. Always
            rendered; harmless when content is short. */}
        {estimates.length > 0 && (
          <div
            aria-hidden
            className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 rounded-b-2xl bg-gradient-to-t from-white via-white/80 to-transparent"
          />
        )}
      </div>
    )
  }

  return (
    <div className={`rounded-2xl border border-zinc-200/60 bg-white shadow-sm transition-all ${isOver ? "ring-2 ring-zinc-400 shadow-lg" : ""}`}>
      {title && (
        <div className="border-b border-zinc-100 bg-zinc-50/50 px-4 py-3 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              {eyebrow && (
                <p className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-zinc-400 leading-none mb-1">
                  {eyebrow}
                </p>
              )}
              <h3 className="font-semibold text-sm text-zinc-900 flex items-center gap-2">
                {accentClassName && (
                  <span
                    aria-hidden
                    className={`size-2 rounded-full ${accentClassName}`}
                  />
                )}
                {title}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              {headerAction}
              <div className="bg-zinc-100 text-zinc-600 px-2.5 py-0.5 rounded-lg text-xs font-semibold">{count ?? 0}</div>
            </div>
          </div>
        </div>
      )}
      <div ref={setNodeRef} className="p-3 space-y-2.5 min-h-[500px] max-h-[600px] overflow-y-auto">
        {estimates.map((estimate) => (
          <DraggableCard
            key={estimate.id}
            estimate={estimate}
            isNew={isNewEstimate?.(estimate)}
            isConsumerLead={sourceOf(estimate) === "consumer_intake"}
            isTotalLoss={estimate.outcome === "total_loss" || estimate.totalLoss === true}
            disabled={!canMove}
            onViewDetails={onViewDetails}
            onCall={onCall}
            onEmail={onEmail}
            onSMS={onSMS}
            onConfirm={onConfirm}
          />
        ))}
        {estimates.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-zinc-400">No estimates</p>
          </div>
        )}
      </div>
    </div>
  )
}
